import useTranslation from 'next-translate/useTranslation'
import {
  OrderFormat,
  OutletState,
  ServiceTime,
  ServiceTimeKindType,
  ErrorCode,
  BoxPickUpStation,
} from '@ancon/wildcat-types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import moment from 'moment'
import { useRouter } from 'next/router'
import { useSearchParams } from 'next/navigation'
import { batch } from 'react-redux'
import createCodedError from '@ancon/wildcat-utils/error/createCodedError'
import noop from 'lodash/noop'
import usePrevious from '@ancon/wildcat-utils/shared/hooks/usePrevious'

import HeadingText from '../../../app/components/HeadingText'
import BodyText from '../../../app/components/BodyText'
import Modal from '../../../app/components/modal/Modal'
import OrderTimeSelectionStep from '../../../outlet/components/orderConfigurationModal/OrderTimeSelectionStep'
import useAppSelector from '../../../../store/hooks/useAppSelector'
import { outletsSelectedOutletListItemSelector } from '../../store/outletsSelectors'
import OrderConfigurationModalHeader from '../../../outlet/components/orderConfigurationModal/OrderConfigurationModalHeader'
import { ModalHeaderProps } from '../../../app/components/modal/ModalHeader'
import {
  AppRoutes,
  AppTestIds,
  OrderFormatSlugMap,
} from '../../../app/constants'
import Button from '../../../app/components/Button'
import useRouteMatch from '../../../app/hooks/useRouteMatch'
import {
  outletFiltersSectionSelector,
  outletFiltersServiceTimeSelector,
  outletListItemSelector,
} from '../../../outlet/store/outletSelector'
import useAppDispatch from '../../../../store/hooks/useAppDispatch'
import {
  outletSetIsVisibleServiceTimeKindSelectionModal,
  outletSetOrderConfigureFilterModal,
  outletSetOutletFilters,
} from '../../../outlet/store/outletSlice'
import getTranslatedError from '../../../app/utils/getTranslatedError'
import { appShowError } from '../../../app/store/appSlice'
import { ErrorModalType } from '../../../app/types'
import BoxPickupSections from '../../../outlet/components/boxPickupOrderConfigureModal/BoxPickupSections'
import { fetchOutletStations } from '../../../outlet/store/outletThunks'
import hasOutletDetailsURLFlag from '../../../outlet/utils/hasOutletDetailsURLFlag'
import isOutletInPreOrderOnlyMode from '../../../outlet/utils/isOutletInPreOrderOnlyMode'
import useGetOrderFormatName from '../../../app/hooks/useGetOrderFormatName'
import getServiceTimeLabel from '../../../app/utils/getServiceTimeLabel'
import PanelOptionTiles from '../../../preOrder/components/newOrderPanel/PanelOptionTiles'
import { GuestOrderHorizonDays } from '../../../preOrder/constants'
import { OrderConfigureFilterMode } from '../../../outlet/types'
import {
  clientContextFetchPendingSelector,
  clientContextIsCompanyUserSelector,
} from '../../../clientContext/store/clientContextSelectors'
import { authCustomerIsLoggedInSelector } from '../../../auth/store/authSelectors'
import getOutletEntryModal from '../../utils/getOutletEntryModal'
import { OutletEntryModal } from '../../types'

import OrderConfigureTimeInfo from './OrderConfigureTimeInfo'
import styles from './NewOrderTimeConfigureModal.module.scss'

const initialServiceTime = {
  kind: ServiceTimeKindType.AsSoonAsPossible,
  time: '',
}

type NewOrderTimeConfigureModalProps = {
  selectedOrderFormat?: OrderFormat
  onClose: () => void
  isOpen?: boolean
}

export default function NewOrderTimeConfigureModal({
  selectedOrderFormat,
  onClose,
  isOpen = false,
}: NewOrderTimeConfigureModalProps) {
  const isBoxPickup = selectedOrderFormat === OrderFormat.BoxPickup
  const isTableOrder = selectedOrderFormat === OrderFormat.TableOrder
  const prevIsBoxPickup = usePrevious(isBoxPickup)
  const { t, lang } = useTranslation('common')
  const router = useRouter()
  const searchParams = useSearchParams()
  const { query } = router
  const dispatch = useAppDispatch()
  const getOrderFormatName = useGetOrderFormatName()
  const [isShowTimePicker, setIsShowTimePicker] = useState(
    !query?.serviceTime ? !!isBoxPickup : false,
  )
  const [serviceTime, setServiceTime] = useState<ServiceTime>(
    isBoxPickup
      ? { kind: ServiceTimeKindType.AtSpecifiedTime, time: '' }
      : initialServiceTime,
  )
  const [selectedSection, setSelectedSection] = useState<BoxPickUpStation>()
  const [sections, setSections] = useState<BoxPickUpStation[]>([])
  const [isSectionsFetching, setIsSectionsFetching] = useState(false)
  const isOutletPage = useRouteMatch([`${AppRoutes.Outlet}/`])
  const outletsOutletListItem = useAppSelector(
    outletsSelectedOutletListItemSelector,
  )
  const outletOutletListItem = useAppSelector(outletListItemSelector)
  const outletFilterServiceTime = useAppSelector(
    outletFiltersServiceTimeSelector,
  )
  const outletFiltersSection = useAppSelector(outletFiltersSectionSelector)
  const isCompanyUser = useAppSelector(clientContextIsCompanyUserSelector)
  const isLoggedIn = useAppSelector(authCustomerIsLoggedInSelector)
  const isClientContextFetchPending = useAppSelector(
    clientContextFetchPendingSelector,
  )
  const outletListItem = isOutletPage
    ? outletOutletListItem
    : outletsOutletListItem
  const isOrderNow = serviceTime.kind === ServiceTimeKindType.AsSoonAsPossible
  const pickerTimeMoment = serviceTime?.time
    ? moment(serviceTime.time)
    : undefined

  const isInPreOrderOnlyMode = isOutletInPreOrderOnlyMode(outletListItem)

  const isFirstStep = !isShowTimePicker || (isBoxPickup && !pickerTimeMoment)

  const isPreOrderOnly = isBoxPickup || isInPreOrderOnlyMode

  useEffect(() => {
    async function showOutletClosedError() {
      const error = await getTranslatedError(
        createCodedError(
          'Restaurant not open yet',
          ErrorCode.RequestedOutletIsNotOpen,
        ),
        undefined,
        lang,
      )
      dispatch(
        appShowError({
          ...error,
          errorModalType: ErrorModalType.OutletNotAvailableForOrders,
        }),
      )
    }

    const hasOutletDetailsFlag = hasOutletDetailsURLFlag(searchParams)

    if (
      isOutletPage &&
      !outletFilterServiceTime &&
      query?.serviceTime === undefined &&
      !hasOutletDetailsFlag
    ) {
      if (outletOutletListItem) {
        const entryModal = getOutletEntryModal({
          isLoggedIn,
          isClientContextFetchPending,
          isCompanyUser,
          isTableOrder,
          isOutletClosed: outletOutletListItem.state === OutletState.Closed,
        })

        if (entryModal === OutletEntryModal.CompanyOrderTime) {
          dispatch(outletSetIsVisibleServiceTimeKindSelectionModal(false))
          dispatch(
            outletSetOrderConfigureFilterModal(
              OrderConfigureFilterMode.OrderTime,
            ),
          )
        } else if (entryModal === OutletEntryModal.OutletClosed) {
          showOutletClosedError()
        } else if (entryModal === OutletEntryModal.ServiceTimeKindSelection) {
          dispatch(outletSetIsVisibleServiceTimeKindSelectionModal(true))
        }
      }
    } else if (
      isOutletPage &&
      outletFilterServiceTime &&
      isBoxPickup &&
      !outletFiltersSection &&
      !hasOutletDetailsFlag
    ) {
      // Show section selection modal
      dispatch(outletSetIsVisibleServiceTimeKindSelectionModal(true))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    outletFilterServiceTime,
    isOutletPage,
    query?.serviceTime,
    dispatch,
    outletOutletListItem,
    lang,
    outletFiltersSection,
    isBoxPickup,
    isCompanyUser,
    isLoggedIn,
    isClientContextFetchPending,
  ])

  useEffect(() => {
    if (prevIsBoxPickup !== isBoxPickup && !query?.serviceTime) {
      setIsShowTimePicker(!!isBoxPickup)
    }
  }, [isBoxPickup, prevIsBoxPickup, query?.serviceTime])

  useEffect(() => {
    // Reset state when modal is closed
    if (!isOpen) {
      setSelectedSection(undefined)
    }
  }, [isOpen])

  useEffect(() => {
    // Fetch sections
    if (isOpen && isBoxPickup && isOutletPage) {
      setIsSectionsFetching(true)
      dispatch(fetchOutletStations({ outletId: outletListItem?.id! }))
        .unwrap()
        .then(({ items }) => {
          setSections(items)
        })
        .catch(noop)
        .finally(() => {
          setIsSectionsFetching(false)
        })
    }
  }, [dispatch, isBoxPickup, isOpen, outletListItem?.id, isOutletPage])

  function handleOnPressBack() {
    setIsShowTimePicker(false)

    if (!isPreOrderOnly) {
      // Leaving the picker without a time goes back to ordering now
      setServiceTime(current => (current.time ? current : initialServiceTime))
    }
  }

  const CustomHeader = useCallback(
    (headerProps: ModalHeaderProps) => (
      <OrderConfigurationModalHeader
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...headerProps}
        onBack={handleOnPressBack}
        isSecondStep={!isFirstStep}
        className={clsx({
          [styles.header]: !isFirstStep,
        })}
      />
    ),
    [isFirstStep, isPreOrderOnly],
  )

  function handleOnChangeServiceTime(newServiceTime: ServiceTime) {
    const isNavigateToOutletPage = !isOutletPage && outletListItem?.urlSlug
    if (isNavigateToOutletPage) {
      const { urlSlug } = outletListItem
      router.push({
        pathname: `${AppRoutes.Outlet}/${urlSlug}`,
        query: {
          orderFormat: OrderFormatSlugMap[selectedOrderFormat as never],
          serviceTime: newServiceTime.time,
        },
      })
    } else {
      setServiceTime(newServiceTime)
      setIsShowTimePicker(false)
    }
  }

  function handleOnClickOrderNow() {
    setServiceTime({
      kind: ServiceTimeKindType.AsSoonAsPossible,
      time: '',
    })
  }

  // Choosing to schedule opens the date and time picker straight away
  function handleOnClickOrderLater() {
    setServiceTime(current => ({
      kind: ServiceTimeKindType.AtSpecifiedTime,
      time:
        current.kind === ServiceTimeKindType.AtSpecifiedTime ? current.time : '',
    }))
    setIsShowTimePicker(true)
  }

  useEffect(() => {
    if (isOpen) {
      const filterServiceTime = outletFilterServiceTime?.time
      const serviceTimeKind =
        isInPreOrderOnlyMode || !!filterServiceTime
          ? ServiceTimeKindType.AtSpecifiedTime
          : ServiceTimeKindType.AsSoonAsPossible

      setServiceTime({
        kind: serviceTimeKind,
        time: filterServiceTime || '',
      })
    }
  }, [isInPreOrderOnlyMode, isOpen, outletFilterServiceTime?.time])

  function handleOnClickScheduleOrder() {
    setIsShowTimePicker(true)
  }

  function handleOnClickContinue() {
    if (isOutletPage) {
      batch(() => {
        dispatch(
          outletSetOutletFilters({
            orderFormat: selectedOrderFormat,
            serviceTime,
            section: selectedSection,
          }),
        )
        dispatch(outletSetIsVisibleServiceTimeKindSelectionModal(false))
      })
    } else if (outletListItem?.urlSlug) {
      const { urlSlug } = outletListItem
      router.push({
        pathname: `${AppRoutes.Outlet}/${urlSlug}`,
        query: {
          orderFormat: OrderFormatSlugMap[selectedOrderFormat as never],
          serviceTime: serviceTime.time,
        },
      })
    }
  }

  function handleOnClose() {
    onClose()
    setServiceTime(initialServiceTime)
    setIsShowTimePicker(false)
  }

  const isContinueButtonDisabled = useMemo(() => {
    const isServiceTimeNotAdded = !isOrderNow && !serviceTime?.time

    if (isBoxPickup) {
      return isServiceTimeNotAdded || !selectedSection
    }

    return isServiceTimeNotAdded
  }, [isBoxPickup, isOrderNow, selectedSection, serviceTime?.time])

  function handleSwitchedToCompany() {
    handleOnClose()

    // The company picks its time from the company calendar instead
    if (isOutletPage) {
      dispatch(
        outletSetOrderConfigureFilterModal(OrderConfigureFilterMode.OrderTime),
      )
    }
  }

  const formatName = getOrderFormatName(selectedOrderFormat)
  const outletName = outletListItem?.name

  function getSubtitle() {
    const parts = isShowTimePicker
      ? [outletName, formatName]
      : [formatName, outletName]

    return parts.filter(Boolean).join(' · ')
  }

  function getContinueLabel() {
    if (isContinueButtonDisabled) {
      return t('startOrder.start')
    }

    return t('startOrder.startWith', {
      format: formatName,
      when: getServiceTimeLabel(serviceTime, t),
    })
  }

  return (
    <Modal
      isOpen={isOpen}
      title={t(isShowTimePicker ? 'orderTimeSwitch.later' : 'startOrder.title')}
      eyebrow={isShowTimePicker ? undefined : t('startOrder.eyebrow')}
      subtitle={getSubtitle()}
      onClose={handleOnClose}
      className={styles.modal}
      mobileModalMode={isShowTimePicker ? 'full-screen' : 'bottom-sheet'}
      CustomModalHeader={CustomHeader}
      preventOnClose={isBoxPickup && isOutletPage}
    >
      {isShowTimePicker ? (
        <OrderTimeSelectionStep
          selectedOrderFormat={selectedOrderFormat!}
          initialServiceTime={serviceTime}
          outletListItem={outletListItem!}
          onApply={handleOnChangeServiceTime}
          onSwitchedToCompany={handleSwitchedToCompany}
        />
      ) : (
        <div className={styles.wrapper}>
          {isPreOrderOnly && (
            <BodyText color="body-1" className={styles.infoText}>
              {t(
                isBoxPickup
                  ? 'newOrderTimeConfigureModal.boxPickup.whenInfo'
                  : 'newOrderTimeConfigureModal.info',
              )}
            </BodyText>
          )}

          {!isPreOrderOnly && (
            <div className={styles.timeTiles}>
              <PanelOptionTiles
                ariaLabel={t('startOrder.title')}
                selectedId={isOrderNow ? 'now' : 'later'}
                onSelect={id =>
                  id === 'now'
                    ? handleOnClickOrderNow()
                    : handleOnClickOrderLater()
                }
                tiles={[
                  {
                    id: 'now',
                    label: t('orderTimeSwitch.orderNow'),
                    hint: t('startOrder.nowHint'),
                  },
                  {
                    id: 'later',
                    label: t('orderTimeSwitch.later'),
                    testId:
                      AppTestIds.Order.Modal.NewOrderTimeConfigureModal
                        .OrderLaterButton,
                    hint: t('startOrder.laterHint', {
                      days: GuestOrderHorizonDays,
                    }),
                  },
                ]}
              />
            </div>
          )}

          {isPreOrderOnly && (
            <HeadingText
              as="h3"
              color="heading-1"
              className={styles.schedulePreOrderTitle}
            >
              {t('orderTimeSwitch.later')}
            </HeadingText>
          )}
          {(!isOrderNow || isBoxPickup) && (
            <section className={styles.dateTimeSection}>
              <OrderConfigureTimeInfo
                serviceTime={serviceTime}
                onClickScheduleOrder={handleOnClickScheduleOrder}
              />
              {isBoxPickup && (
                <BoxPickupSections
                  onChangeSection={setSelectedSection}
                  selectedSection={selectedSection}
                  sections={sections}
                  isLoading={isSectionsFetching}
                />
              )}
            </section>
          )}
          <Button
            className={styles.continueButton}
            disabled={isContinueButtonDisabled}
            onClick={handleOnClickContinue}
            data-testid={
              AppTestIds.Order.Modal.NewOrderTimeConfigureModal.ContinueButton
            }
          >
            {getContinueLabel()}
          </Button>
        </div>
      )}
    </Modal>
  )
}
